'use client';

import Image from 'next/image';
import dynamic from 'next/dynamic';
import React, {useState, useRef, useEffect, useCallback} from 'react';
import {Icon} from '@udemy-v2/icons';
import {useI18n} from '@udemy-v2/i18n';

import styles from './feature-selector.module.scss';
import {FeatureCard} from './feature-card';
import {FeaturePagination} from './feature-pagination';

const Lottie = dynamic(() => import('lottie-react'), {ssr: false});

import pp1 from './animations/pp1.json';
import pp2 from './animations/pp2.json';
import pp3 from './animations/pp3.json';

type FeatureKey = 'experts' | 'learning' | 'interactive';

interface FeatureData {
    title: string;
    description: string;
    animationData: object;
    mobileImage: string;
}

const FeatureSelector: React.FC = () => {
    const {gettext} = useI18n();
    const [selectedFeature, setSelectedFeature] = useState<FeatureKey>('experts');
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showLeftButton, setShowLeftButton] = useState(false);
    const [showRightButton, setShowRightButton] = useState(true);

    const features: Record<FeatureKey, FeatureData> = {
        experts: {
            title: gettext('Learn from real-world experts'),
            description: gettext(
                'Instructors on Udemy love what they teach and have the ratings to show they know their stuff.',
            ),
            animationData: pp1,
            mobileImage: '/indeed/features/pp1mobile.png',
        },
        learning: {
            title: gettext('Choose how you learn'),
            description: gettext(
                'Learn how you like, at your own pace, on your schedule, on desktop or mobile.',
            ),
            animationData: pp2,
            mobileImage: '/indeed/features/pp2mobile.png',
        },
        interactive: {
            title: gettext('Get interactive'),
            description: gettext(
                'Go beyond video lectures with coding exercises, practice tests, quizzes, and Q&A.',
            ),
            animationData: pp3,
            mobileImage: '/indeed/features/pp3mobile.png',
        },
    };

    const featureKeys = Object.keys(features) as FeatureKey[];

    const handleScroll = useCallback(() => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const {scrollLeft, scrollWidth, clientWidth} = container;

            // Update navigation buttons visibility
            setShowLeftButton(scrollLeft > 0);
            setShowRightButton(scrollLeft < scrollWidth - clientWidth - 10);

            // Update selected feature based on scroll position
            const slideWidth = container.offsetWidth;
            const currentIndex = Math.round(scrollLeft / slideWidth);
            const newFeature = featureKeys[currentIndex];

            if (newFeature && newFeature !== selectedFeature) {
                setSelectedFeature(newFeature);
            }
        }
    }, [selectedFeature, featureKeys]);

    const handleFeatureSelect = (feature: FeatureKey) => {
        setSelectedFeature(feature);
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const slideWidth = container.offsetWidth;
            const slideIndex = featureKeys.indexOf(feature);
            container.scrollTo({
                left: slideWidth * slideIndex,
                behavior: 'smooth',
            });
        }
    };

    const scrollToSlide = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const slideWidth = container.offsetWidth;
            const currentScroll = container.scrollLeft;
            const targetScroll =
                direction === 'left' ? currentScroll - slideWidth : currentScroll + slideWidth;

            container.scrollTo({
                left: targetScroll,
                behavior: 'smooth',
            });
        }
    };

    const paginationFeatures = Object.entries(features).map(([key]) => ({
        key: key as FeatureKey,
        title: '',
    }));

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            // Initial check for button visibility
            handleScroll();

            // Add resize observer to handle window resize
            const resizeObserver = new ResizeObserver(() => {
                handleScroll();
            });
            resizeObserver.observe(container);

            return () => {
                container.removeEventListener('scroll', handleScroll);
                resizeObserver.disconnect();
            };
        }
    }, [handleScroll]);

    return (
        <section className={styles.featureContainer}>
            <div className={styles.featureOptionsContainer}>
                <FeatureCard
                    title={features.experts.title}
                    description={features.experts.description}
                    isSelected={selectedFeature === 'experts'}
                    onClick={() => handleFeatureSelect('experts')}
                    className={styles.featureCard}
                />
                <FeatureCard
                    title={features.learning.title}
                    description={features.learning.description}
                    isSelected={selectedFeature === 'learning'}
                    onClick={() => handleFeatureSelect('learning')}
                    className={styles.featureCard}
                />
                <FeatureCard
                    title={features.interactive.title}
                    description={features.interactive.description}
                    isSelected={selectedFeature === 'interactive'}
                    onClick={() => handleFeatureSelect('interactive')}
                    className={styles.featureCard}
                />
            </div>
            <div className={styles.desktopContent}>
                <Lottie
                    className={styles.featureImage}
                    animationData={features[selectedFeature].animationData}
                    loop={false}
                    aria-label={`Animation for ${features[selectedFeature].title}`}
                />
            </div>
            <div className={styles.mobileContent}>
                <div className={styles.scrollButtonContainer}>
                    {showLeftButton && (
                        <button
                            className={styles.scrollButton}
                            onClick={() => scrollToSlide('left')}
                            aria-label="Previous feature"
                        >
                            <Icon glyph="arrow-left" label="Previous feature" />
                        </button>
                    )}
                    <div
                        ref={scrollContainerRef}
                        className={styles.mobileScrollContainer}
                        onScroll={handleScroll}
                    >
                        {Object.entries(features).map(([key, feature]) => (
                            <div key={key} className={styles.mobileSlide}>
                                <Image
                                    src={feature.mobileImage}
                                    alt={feature.title}
                                    className={styles.featureImage}
                                    width={327}
                                    height={327}
                                    priority={key === 'experts'}
                                />
                                <h3 className={styles.mobileTitle}>{feature.title}</h3>
                                <p className={styles.mobileDescription}>{feature.description}</p>
                            </div>
                        ))}
                    </div>
                    {showRightButton && (
                        <button
                            className={styles.scrollButton}
                            onClick={() => scrollToSlide('right')}
                            aria-label="Next feature"
                        >
                            <Icon glyph="arrow-right" label="Next feature" />
                        </button>
                    )}
                </div>
                <FeaturePagination
                    features={paginationFeatures}
                    selectedFeature={selectedFeature}
                    onSelect={handleFeatureSelect}
                />
            </div>
        </section>
    );
};

export {FeatureSelector};
